import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import React, { useState } from 'react';
import moment from 'moment';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import {
  useListExecutionsQuery,
  useGetAvailableJobsQuery,
  useListJobsQuery,
} from 'store/app/entities/BatchSlice';
import VisibilityIcon from 'mdi-react/VisibilityIcon';
import JobDetailsDrawer from './JobDetailsDrawer';
import { I18n } from 'utils/i18n';
import { useMediaQuery } from '@mui/system';

// Helper to safely extract batchConfigId value (handles both string and object)
const getBatchConfigIdLabel = batchConfigId => {
  if (!batchConfigId) return null;
  if (typeof batchConfigId === 'string') return batchConfigId;
  return batchConfigId._id || batchConfigId.name || 'Batch Config';
};

const statusColorMap = {
  running: 'primary',
  success: 'success',
  failure: 'error',
  timeout: 'warning',
  pending: 'default',
};

const JobExecutionHistory = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [jobName, setJobName] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    // Default to 7 days ago
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Calculate days from selected date to today
  const days = fromDate ? Math.ceil((new Date() - new Date(fromDate)) / (1000 * 60 * 60 * 24)) : 7;

  const { data: availableJobs } = useGetAvailableJobsQuery();
  const { data: jobsData } = useListJobsQuery();

  // Handle different response structures and use listJobs as fallback
  let jobNames = [];
  if (availableJobs) {
    if (Array.isArray(availableJobs)) {
      jobNames = availableJobs;
    } else if (availableJobs.jobs && Array.isArray(availableJobs.jobs)) {
      jobNames = availableJobs.jobs;
    }
  }

  // Fallback: extract job names from listJobs query
  if (jobNames.length === 0 && jobsData?.jobs) {
    jobNames = jobsData.jobs.map(job => job.jobName || job.name).filter(Boolean);
  }

  const {
    data: executionsData,
    isLoading,
    refetch,
  } = useListExecutionsQuery({
    jobName: jobName || undefined,
    status: status || undefined,
    days,
    limit: perPage,
    offset: page * perPage,
  });

  const executions = executionsData?.executions || [];
  const totalCount = executionsData?.total || 0;

  // Note: WebSocket subscription is handled at parent level (AdminBatchScene)
  // RTK Query will automatically refetch when 'JobExecution' tag is invalidated

  const columns = [
    {
      field: 'jobName',
      width: 200,
      headerName: I18n.t('AdminBatchScene.JobName') || 'Job Name',
    },
    {
      field: 'status',
      width: 120,
      headerName: I18n.t('AdminBatchScene.Status') || 'Status',
      renderCell: params => (
        <Chip
          label={params.value}
          color={statusColorMap[params.value] || 'default'}
          size="small"
          variant="soft"
        />
      ),
    },
    {
      field: 'startedAt',
      width: 180,
      headerName: I18n.t('AdminBatchScene.StartedAt') || 'Started At',
      renderCell: params =>
        params.value ? moment(params.value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      field: 'duration',
      width: 120,
      headerName: I18n.t('AdminBatchScene.Duration') || 'Duration',
      renderCell: params => (params.value ? `${(params.value / 1000).toFixed(1)}s` : '-'),
    },
    {
      field: 'workerId',
      width: 150,
      headerName: I18n.t('AdminBatchScene.WorkerId') || 'Worker ID',
      renderCell: params => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'batchConfigId',
      width: 150,
      headerName: I18n.t('AdminBatchScene.BatchConfig') || 'Batch Config',
      renderCell: params =>
        params.value ? (
          <Chip label={getBatchConfigIdLabel(params.value)} size="small" variant="outlined" />
        ) : (
          '-'
        ),
    },
    {
      field: 'actions',
      width: 100,
      headerName: I18n.t('AdminBatchScene.Actions') || 'Actions',
      sortable: false,
      renderCell: params => (
        <IconButton
          size="small"
          onClick={() => {
            setSelectedExecution(params.row);
            setIsDetailsOpen(true);
          }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Box
        sx={{ display: 'flex', gap: 2, marginBottom: 3, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>{I18n.t('AdminBatchScene.FilterByJob') || 'Filter by Job'}</InputLabel>
          <Select value={jobName} onChange={e => setJobName(e.target.value)} label="Filter by Job">
            <MenuItem value="">{I18n.t('AdminBatchScene.AllJobs') || 'All Jobs'}</MenuItem>
            {jobNames.map(name => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>{I18n.t('AdminBatchScene.FilterByStatus') || 'Filter by Status'}</InputLabel>
          <Select value={status} onChange={e => setStatus(e.target.value)} label="Filter by Status">
            <MenuItem value="">{I18n.t('AdminBatchScene.AllStatuses') || 'All Statuses'}</MenuItem>
            <MenuItem value="running">Running</MenuItem>
            <MenuItem value="success">Success</MenuItem>
            <MenuItem value="failure">Failure</MenuItem>
            <MenuItem value="timeout">Timeout</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={I18n.t('AdminBatchScene.FromDate') || 'From Date'}
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          sx={{ width: 180, colorScheme: prefersDarkMode ? 'dark' : 'light', padding: 0 }}
          slotProps={{
            inputLabel: { shrink: true },
            input: { sx: { px: '14px', py: '8.5px' } },
            htmlInput: {
              min: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              max: new Date().toISOString().split('T')[0],
            },
          }}
        />
        <Button variant="outlined" onClick={() => refetch()}>
          {I18n.t('AdminBatchScene.Refresh') || 'Refresh'}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <OmmnioDataGrid
          loading={isLoading}
          columns={columns}
          rows={executions}
          rowCount={totalCount}
          paginationMode="server"
          getRowId={r => r._id || r.executionId}
          page={page}
          pageSize={perPage}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          disableEvenOdd
        />
      )}

      <JobDetailsDrawer
        execution={selectedExecution}
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedExecution(null);
        }}
        onRefresh={refetch}
      />
    </Box>
  );
};

export default JobExecutionHistory;
